/**
 * Lazy Analytics Loader
 * GA4, Meta Pixel, Metricool, and Tally load after user interaction or idle
 * so third-party scripts don't compete with React on the critical path.
 * Ahrefs is primarily injected in index.html <head> for install recheck;
 * loadAhrefs() is a production-only idempotent fallback.
 */

const METRICOOL_HASH = "8e1842adce082921f46337795c672fcd";
const META_PIXEL_ID = "1018103057361682";
const AHREFS_DATA_KEY = "oXNUNzJGeWPu4d6xdtK+fQ";
const GA_MEASUREMENT_ID =
  import.meta.env.VITE_GA_MEASUREMENT_ID ||
  import.meta.env.VITE_GA_TRACKING_ID ||
  "G-D1JQVWYJM6";

let analyticsLoaded = false;

function isProductionDomain(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "juaperrone.com" || h === "www.juaperrone.com";
}

function loadMetaPixel(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if ((window as { fbq?: unknown }).fbq) return;

  const fbq = function (...args: unknown[]) {
    const q = (fbq as { queue?: unknown[] }).queue ?? [];
    if ((fbq as { callMethod?: (...a: unknown[]) => void }).callMethod) {
      (fbq as { callMethod: (...a: unknown[]) => void }).callMethod(...args);
    } else {
      q.push(args);
    }
    (fbq as { queue: unknown[] }).queue = q;
  };
  (fbq as { queue: unknown[]; loaded: boolean; version: string }).queue = [];
  (fbq as { loaded: boolean }).loaded = true;
  (fbq as { version: string }).version = "2.0";
  (window as { fbq?: typeof fbq; _fbq?: typeof fbq }).fbq = fbq;
  (window as { _fbq?: typeof fbq })._fbq = fbq;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);

  (window as { fbq: (cmd: string, id: string, opts?: unknown) => void }).fbq(
    "init",
    META_PIXEL_ID,
  );
  (window as { fbq: (cmd: string, ...args: unknown[]) => void }).fbq(
    "track",
    "PageView",
  );
}

function loadGoogleAnalytics(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!isProductionDomain()) return;
  if (typeof window.gtag === "function") return;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.crossOrigin = "anonymous";
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    cookie_domain: "juaperrone.com",
  });
}

function loadMetricool(): void {
  if (document.querySelector('script[src*="metricool.com"]')) return;

  const script = document.createElement("script");
  script.src = "https://tracker.metricool.com/resources/be.js";
  script.async = true;
  script.onload = () => {
    if (typeof window.beTracker !== "undefined") {
      window.beTracker.t({ hash: METRICOOL_HASH });
    }
  };
  document.head.appendChild(script);
}

function loadTally(): void {
  if (document.querySelector('script[src*="tally.so"]')) {
    if (typeof (window as Window & { Tally?: { loadEmbeds: () => void } }).Tally !== "undefined") {
      (window as Window & { Tally?: { loadEmbeds: () => void } }).Tally!.loadEmbeds();
    }
    return;
  }

  const script = document.createElement("script");
  script.src = "https://tally.so/widgets/embed.js";
  script.async = true;
  const onReady = () => {
    const w = window as Window & { Tally?: { loadEmbeds: () => void } };
    if (typeof w.Tally !== "undefined") {
      w.Tally.loadEmbeds();
    } else {
      document.querySelectorAll<HTMLIFrameElement>("iframe[data-tally-src]:not([src])").forEach((el) => {
        el.src = el.dataset.tallySrc ?? "";
      });
    }
  };
  script.onload = onReady;
  script.onerror = onReady;
  document.head.appendChild(script);
}

/** Ahrefs Web Analytics — head install is primary; this is idempotent fallback. */
function loadAhrefs(): void {
  if (typeof document === "undefined") return;
  if (!isProductionDomain()) return;
  if (document.querySelector('script[src*="analytics.ahrefs.com"]')) return;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://analytics.ahrefs.com/analytics.js";
  script.setAttribute("data-key", AHREFS_DATA_KEY);
  document.head.appendChild(script);
}

export function loadAllAnalytics(): void {
  if (analyticsLoaded) return;
  analyticsLoaded = true;

  loadGoogleAnalytics();
  loadMetaPixel();
  loadMetricool();
  loadTally();
  loadAhrefs();
}

export function initAnalyticsLoader(): void {
  const events = ["click", "scroll", "keydown", "touchstart"];

  const loadOnInteraction = () => {
    loadAllAnalytics();
    events.forEach((event) => {
      window.removeEventListener(event, loadOnInteraction, { capture: true });
    });
  };

  events.forEach((event) => {
    window.addEventListener(event, loadOnInteraction, {
      capture: true,
      passive: true,
    });
  });

  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(() => loadAllAnalytics(), { timeout: 4000 });
  } else {
    setTimeout(loadAllAnalytics, 4000);
  }
}

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    beTracker: {
      t: (config: { hash: string }) => void;
    };
  }
}
